import { useState } from "react";
import { Mail, X, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import RichTextEditor from "../components/RichTextEditor";

const TagInput = ({ items, onChange, color, placeholder, testIdPrefix }) => {
  const colorMap = {
    green: "bg-emerald-100 text-emerald-800 text-emerald-600 hover:text-emerald-900",
    blue: "bg-blue-100 text-blue-800 text-blue-600 hover:text-blue-900",
    gray: "bg-slate-200 text-slate-700 text-slate-500 hover:text-slate-800",
  };
  const c = colorMap[color] || colorMap.green;
  const [bg, text, removeText, removeHover] = c.split(" ");

  return (
    <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 rounded-md border min-h-[38px] items-center" data-testid={`email-preview-${testIdPrefix}`}>
      {(items || []).map((addr, i) => (
        <span key={`${testIdPrefix}-${addr}-${i}`} className={`inline-flex items-center gap-1 ${bg} ${text} text-xs font-medium px-2 py-1 rounded-full`}>
          {addr}
          <button
            type="button"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            className={`${removeText} ${removeHover}`}
            data-testid={`remove-${testIdPrefix}-${i}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        type="email"
        placeholder={items?.length ? "" : placeholder}
        className="flex-1 min-w-[140px] bg-transparent outline-none text-sm"
        data-testid={`add-${testIdPrefix}-input`}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === "," || e.key === "Tab") && e.target.value.trim()) {
            e.preventDefault();
            const val = e.target.value.trim().replace(/,$/, "");
            if (val && val.includes("@")) {
              onChange([...(items || []), val]);
              e.target.value = "";
            }
          }
        }}
        onBlur={(e) => {
          const val = e.target.value.trim().replace(/,$/, "");
          if (val && val.includes("@")) {
            onChange([...(items || []), val]);
            e.target.value = "";
          }
        }}
      />
    </div>
  );
};

const EmailPreviewDialog = ({ open, onClose, emailPreview, setEmailPreview, onSend, sending }) => {
  if (!emailPreview) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5 text-blue-600" />
            Review & Send Email
          </DialogTitle>
          <DialogDescription>
            Review the email below before sending. Variables have been replaced with actual transaction data.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {emailPreview.email_sent && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              This email has already been sent.
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">To</Label>
            <TagInput
              items={emailPreview.recipients}
              onChange={(v) => setEmailPreview({ ...emailPreview, recipients: v })}
              color="green"
              placeholder="Add recipient email..."
              testIdPrefix="recipients"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">CC</Label>
            <TagInput
              items={emailPreview.cc}
              onChange={(v) => setEmailPreview({ ...emailPreview, cc: v })}
              color="blue"
              placeholder="Add CC email..."
              testIdPrefix="cc"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">BCC</Label>
            <TagInput
              items={emailPreview.bcc}
              onChange={(v) => setEmailPreview({ ...emailPreview, bcc: v })}
              color="gray"
              placeholder="Add BCC email..."
              testIdPrefix="bcc"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">Subject</Label>
            <Input
              value={emailPreview.subject || ""}
              onChange={(e) => setEmailPreview({ ...emailPreview, subject: e.target.value })}
              className="h-9 text-sm"
              placeholder="Email subject..."
              data-testid="email-preview-subject"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">Body</Label>
            <RichTextEditor
              value={emailPreview.body || ""}
              onChange={(val) => setEmailPreview({ ...emailPreview, body: val })}
              placeholder="Email body..."
              minHeight="150px"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="email-preview-cancel">
            Cancel
          </Button>
          <Button
            onClick={onSend}
            disabled={sending || emailPreview?.email_sent || !emailPreview?.recipients?.length}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
            data-testid="email-preview-send"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sending..." : "Confirm & Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreviewDialog;
